'use strict';
import {NoteState} from '../../system/enums/NoteState';
import template from './project-import.html';
import {logger} from './logger';

const angular = require('angular');
const {fromJson, isObject, isString, toJson} = angular;


ProjectImportController.$inject = [
  '$modal',
  '$scope',
  '$miscUtils',
  'papaParse',
  '$toast',
  '$ngRedux',
  '$download',
  '$help',
  'AuthService',
  'CsvImportService',
  'ProjectImportExportService',
  'NotificationService',
  'FirebaseQueueService',
  'fetchUtils',
  'project',
  'assetLevel',
  'type',
];

CacheTemplates.$inject = [
  '$templateCache',
];

export function CacheTemplates($templateCache) {
  $templateCache.put('projects/project-import.html', template);
}

/* eslint-disable max-len */
const inviteCsvTemplate = 'milestone_name,milestone_description,task_name,task_description,task_scheduled,task_use_time,task_scheduled_comment,task_status,requirement_name,requirement_description,requirement_type,requirement_status,file_requirement_max,file_requirement_min,file_requirement_enforced';
const inviteCsvInstructions = "During import, Milestones, Tasks, and Requirements are matched by name so to avoid unexpected results we recommend that\nyou do not intentionally create duplicate named tasks under the same milestone or duplicated named requirements under the same task.\n\nIf you leave milestone blank the task on that row will be included under the Misc header (no milestone).\n\nYou may include the same milestone or task on multiple rows to import multiple tasks under the same milestone\nand/or multiple requirements under the same task.\n\nRequirements are optional but if you include one, it must be associated with a task on the same row.\n\nDuplicate task names under different milestones create different tasks under each milestone.\nThe same applies to duplicate requirements names under different task names.\n\nThe import system currently does not support importing duplicates under the same milestone or task by design.\nThis means that you cannot create two of the same named task under the same milestone.\nThis will only create the named task once. This is intended to support including multiple requirements under a single task.\nSimilarly, you cannot create duplicate requirements under the same task. You will receive an error message.\n\nBe advised that if you export a project that does have duplicates (same task under same milestone, same req under same task)\nthat you will not be able to include those duplicates when you import it into another project.\n\n\nTo use the import system to update task dates you must include the date in following formats\nFor a date only use MM/DD/YYYY\nFor a date and time use MM/DD/YYYY HH:MM\nBe sure to change the task_use_time column to true or false to be sure that your dates are imported correctly.";

/* eslint-enable max-len */

export function ProjectImportController(
  $modal,
  $scope,
  $miscUtils,
  papaParse,
  $toast,
  $ngRedux,
  $download,
  $help,
  AuthService,
  csvImportService,
  ProjectImportExportService,
  NotificationService,
  FirebaseQueueService,
  fetchUtils,
  project,
  assetLevel,
  type,
) {

  const vm = $scope.projectImport = this;
  vm.data = null;
  vm.csvData = null;
  vm.errors = null;
  if (type === 'asset')
    vm.templateOptions = {addToExisting: true};

  vm.readCsvData = readCsvData;
  vm.parseData = parseData;
  vm.cancel = $modal.cancel.bind($modal);
  vm.save = $miscUtils.blockPromise(save);
  vm.downloadTemplate = downloadTemplate;
  vm.viewInstructions = viewInstructions;

  function downloadTemplate() {
    $download.downloadAsTextFile(inviteCsvTemplate, 'project import.csv');
  }

  function viewInstructions() {
    $help.showTextModal('Project Import CSV Instructions', inviteCsvInstructions);
  }

  // send the validated list to the server for creation
  function save() {
    if (!vm.data) {
      $toast.showI18nMessage({
        messageId: 'projects.import.no.data.message',
        type: $toast.TOAST_TYPES.error,
      });
      return Promise.reject(null);
    } else {

      const body = {rows: vm.data};
      if (type) body.type = type;
      if (vm.templateOptions) body.templateOptions = vm.templateOptions;
      const request = {
        method: 'POST',
        mode: 'cors',
        cache: 'default',
        contentType: 'json',
        body: body,
      };

      const collection = assetLevel ? 'asset-projects' : 'projects';
      const url = `${fetchUtils.API_URL}/api/${collection}/${project}/_import`;
      const requestPromise = fetchUtils.rawRequestWithAuth(url, request);

      const state = $ngRedux.getState().model;
      const projectName = assetLevel && state.asset
        ? `${state.project.name} | ${state.asset.name}`
        : state.project.name;

      // TODO(i18n)
      NotificationService.addNotification({
        name: 'Import ' + projectName + ' data',
        nameId: 'projects.import.progress.message',
        nameParams: {name: projectName},
        completeMessageId: 'projects.import.success.message',
        errorMessageId: 'projects.import.failure.message',
        removal: NotificationService.REMOVAL.ALWAYS, // may want to use NEVER here. play with this, get feedback
        promise: requestPromise,
        audio: {
          [NoteState.COMPLETE]: true,
          [NoteState.ERROR]: true,
        },
        toasts: {
          [NoteState.COMPLETE]: true,
          [NoteState.ERROR]: true,
        },
      });

      return requestPromise.then(
        _onSuccess,
        () => _onError({errors: ['Network connection error. Please try again later.']}),
      );
    }
  }

  function _onSuccess(response) {
    if (response.ok) {
      // logger.log('success!');
      return $modal.hide();
    } else {
      return _onError(typeof response.data === 'object' ? response.data || {} : {});
    }
  }

  function _onError(error) {
    logger.error({err: error}, 'errors occurred');

    if (isObject(error)) {
      $scope.$evalAsync(function () {
        vm.errors = null;
        if (isString(error.errors)) {
          try {
            vm.errors = fromJson(error.errors); // should be a JSON Array
          } catch (e) {
          }
        }

        if (!vm.errors)
          vm.errors = ["An error occurred processing your request"]; // TODO(i18n): convert to a message and translate
      });
    }

    // Promise rejection is required so that the stateful-button in the view will also display an error instead of success.
    return Promise.reject(error);
  }

  function readCsvData(fileContents) {
    vm.csvData = fileContents;
    parseData();
  }

  function parseData() {
    if (!vm.csvData) {
      vm.data = null;
    } else {
      vm.parsedData = papaParse.parse(vm.csvData, {skipEmptyLines: true});

      if (!_testForErrors(vm.parsedData) && _detectHeaders(vm.parsedData)) {
        // test for headers and, if so, map and return the data
        vm.data = csvImportService.mapWithHeaders(vm.parsedData, ProjectImportExportService.HEADERS);

        if (_testForErrors(vm.parsedData)) {
          vm.data = null;
        }

      } else {
        // TODO: enter UI mapping mode to allow the user to specify which columns are the required headers.
        logger.warn('either encountered an error or failed to detect headers?');
      }

      logger.warn({err: vm.parsedData ? vm.parsedData.errors : null}, 'errors:');
      logger.debug({csvData: vm.csvData}, 'csv data');
      logger.debug({parsedData: vm.parsedData}, 'parsed data');
      logger.debug({data: vm.data}, 'processed data');
    }
  }

  function _testForErrors(parsedData) {
    if (parsedData.errors && parsedData.errors.length > 0) {
      vm.errors = parsedData.errors;
      return true;
    } else {
      vm.errors = null;
      return false;
    }
  }

  function _detectHeaders(parsedData) {
    if (!parsedData.data || !parsedData.data[0]) return false;
    let foundMilestone = false;
    let foundTask = false;
    let foundReq = false;

    for (let i = 0, length = parsedData.data[0].length; i < length; i++) {
      const header = parsedData.data[0][i].toLowerCase().replace(/\s/g, "_");
      if (header === 'milestone_name') {
        foundMilestone = true;
      } else if (header === 'task_name') {
        foundTask = true;
      } else if (header === 'requirement_name') {
        foundReq = true;
      }

      if (foundMilestone && foundTask && foundReq)
        break;
    }

    if (foundReq && !foundTask) {
      // TODO: technically this is a different error.
      return false;
    } else if (foundTask || foundMilestone) {
      return true;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/projects/modals/ProjectImportController.js